import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FlaskConical, ChevronRight, Sparkles } from "lucide-react";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

/**
 * LabFromRxCTA — surfaces lab tests recommended by a doctor (extracted by Gemini
 * from the consultation Rx text) as a one-tap cart pre-load.
 *
 * Props:
 *  - tests: [{ id, name, selling_price, category }]
 *  - context: "prescription" | "consultation"
 *  - showAlways: boolean (default false) — if true, renders even with empty list
 */
const LabFromRxCTA = ({ tests = [], context = "prescription" }) => {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState(() => new Set((tests || []).map((t) => t.id)));

    if (!tests || tests.length === 0) return null;

    const toggle = (id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const total = tests.filter((t) => selected.has(t.id)).reduce((s, t) => s + Number(t.selling_price || 0), 0);

    const book = () => {
        const ids = Array.from(selected);
        if (ids.length === 0) return;
        // Pre-load lab cart in sessionStorage so LabBooking picks them up
        const cart = tests
            .filter((t) => selected.has(t.id))
            .map((t) => ({ key: `test-${t.id}`, test_id: t.id, name: t.name, price: t.selling_price }));
        sessionStorage.setItem("lab-cart", JSON.stringify(cart));
        navigate("/lab-tests/book");
    };

    return (
        <section
            className="rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50/70 to-white p-5 dark:from-emerald-950/30 dark:to-zinc-900 dark:border-white/10"
            data-testid="lab-from-rx-cta"
        >
            <div className="flex items-center gap-2">
                <FlaskConical className="h-5 w-5 text-emerald-700" />
                <p className="text-xs font-semibold uppercase tracking-wider text-emerald-800">Recommended lab tests</p>
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                    <Sparkles className="h-2.5 w-2.5" /> AI-extracted
                </span>
            </div>
            <p className="mt-2 text-sm text-zinc-700 dark:text-zinc-300">
                Your doctor recommended {tests.length} lab test{tests.length === 1 ? "" : "s"}{context === "consultation" ? " in this consultation" : " in this prescription"}. Book in one tap with free home collection.
            </p>

            {!open ? (
                <button
                    onClick={() => setOpen(true)}
                    className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-emerald-700 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-800"
                    data-testid="lab-from-rx-open-btn"
                >
                    Review and book → <ChevronRight className="h-3.5 w-3.5" />
                </button>
            ) : (
                <div className="mt-4" data-testid="lab-from-rx-picker">
                    <ul className="space-y-2">
                        {tests.map((t) => (
                            <li key={t.id} className="flex items-center justify-between gap-2 rounded-md border border-emerald-100 bg-white p-2 dark:bg-white/5 dark:border-white/10">
                                <label className="flex flex-1 cursor-pointer items-center gap-2">
                                    <input
                                        type="checkbox"
                                        checked={selected.has(t.id)}
                                        onChange={() => toggle(t.id)}
                                        data-testid={`lab-from-rx-check-${t.id}`}
                                    />
                                    <span className="min-w-0">
                                        <span className="block truncate text-sm font-medium text-zinc-900 dark:text-zinc-50">{t.name}</span>
                                        {t.category && <span className="text-[10px] text-zinc-500">{t.category}</span>}
                                    </span>
                                </label>
                                <span className="text-sm font-semibold text-zinc-900 dark:text-zinc-50">{inr(t.selling_price)}</span>
                            </li>
                        ))}
                    </ul>
                    <div className="mt-3 flex items-center justify-between gap-2">
                        <p className="text-xs text-zinc-500">
                            {Array.from(selected).length} selected · Total {inr(total)}
                        </p>
                        <button
                            onClick={book}
                            disabled={selected.size === 0}
                            className="inline-flex items-center gap-1.5 rounded-md bg-emerald-700 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-800 disabled:opacity-40"
                            data-testid="lab-from-rx-book-btn"
                        >
                            Book {selected.size > 0 ? selected.size : ""} test{selected.size === 1 ? "" : "s"} →
                        </button>
                    </div>
                </div>
            )}
        </section>
    );
};

export default LabFromRxCTA;
